import {
  IAssessment,
  IAssessmentDto,
  IAssessmentInputs,
  IQuestionSet
} from "./main.interfaces";
import {
  AmountOfStructuredInputData,
  ApplicationStability,
  ApplicationsUsed,
  DecisionDifficulty,
  InputDataStructure,
  InputScanned,
  NoneFeasabilityResult,
  ProcessFrequency,
  ProcessPeaks,
  ProcessStability,
  ProcessSteps,
  RemoteDesktopApplication
} from "./assessment.calc";

export function makeEmptyAssessmentObject(): IAssessment {
  return {
    name: "",
    id: undefined,
    outputs: {
      easeOfImplementation: NaN,
      benefitSuitability: NaN,
      bandwidthFreed: NaN,
      errorReduction: NaN,
      averageHandlingTimeReduction: NaN,
      effortToImplement: NaN
    },
    inputs: {
      inputDataStructure: InputDataStructure.DIGITAL_AND_STRUCTURED,
      processStability: NaN,
      applicationStability: NaN,
      processFrequency: NaN,
      frequencyVolume: NaN,
      averageRuntime: NaN,
      humanErrorPercentage: NaN,
      processPeaks: NaN,
      processSteps: NaN,
      decisionDifficulty: NaN,
      unfinishedProcessPercentage: NaN,
      applicationsUsed: NaN,
      remoteDesktopApplication: NaN,
      digitalInputPercentage: NaN,
      inputScanned: NaN,
      amountOfStructuredInputData: NaN
    },
    creator: "",
    createdAt: new Date(),
    organization: "",
    lastEditedAt: "",
    lastEditedBy: ""
  };
}

export function makeSimpleDigitalAssessmentObject(): IAssessment {
  return {
    name: "",
    id: undefined,
    outputs: {
      easeOfImplementation: NaN,
      benefitSuitability: NaN,
      bandwidthFreed: NaN,
      errorReduction: NaN,
      averageHandlingTimeReduction: NaN,
      effortToImplement: NaN
    },
    inputs: {
      inputDataStructure: InputDataStructure.DIGITAL_AND_STRUCTURED,
      processStability: ProcessStability.NO_CHANGE,
      applicationStability: ApplicationStability.NO_CHANGE,
      processFrequency: ProcessFrequency.DAILY,
      frequencyVolume: 1,
      averageRuntime: 120,
      humanErrorPercentage: 5,
      processPeaks: ProcessPeaks.NO_PEAKS,
      processSteps: ProcessSteps.FIFTEEN_TO_TWENTYFIVE,
      decisionDifficulty: DecisionDifficulty.NO_DECISIONS,
      unfinishedProcessPercentage: 5,
      applicationsUsed: ApplicationsUsed.TWO_TO_THREE,
      remoteDesktopApplication: RemoteDesktopApplication.NO,
      digitalInputPercentage: 100,
      inputScanned: InputScanned.NO,
      amountOfStructuredInputData: AmountOfStructuredInputData.SIXTY_TO_EIGHTY
    },
    creator: "",
    createdAt: new Date(),
    organization: "",
    lastEditedAt: "",
    lastEditedBy: "",
    implemented: false
  };
}

export function makeInputsFromQuestions(
  questions: IQuestionSet
): IAssessmentInputs {
  return {
    inputDataStructure: questions.inputDataStructure.answerValue,
    processStability: questions.processStability.answerValue,
    applicationStability: questions.applicationStability.answerValue,
    processFrequency: questions.processFrequency.answerValue,
    frequencyVolume: questions.frequencyVolume.answerValue,
    averageRuntime: questions.averageRuntime.answerValue,
    humanErrorPercentage: questions.humanErrorPercentage.answerValue,
    processPeaks: questions.processPeaks.answerValue,
    processSteps: questions.processSteps.answerValue,
    decisionDifficulty: questions.decisionDifficulty.answerValue,
    unfinishedProcessPercentage:
      questions.unfinishedProcessPercentage.answerValue,
    applicationsUsed: questions.applicationsUsed.answerValue,
    remoteDesktopApplication: questions.remoteDesktopApplication.answerValue,
    digitalInputPercentage: questions.digitalInputPercentage.answerValue,
    inputScanned: questions.inputScanned.answerValue,
    amountOfStructuredInputData:
      questions.amountOfStructuredInputData.answerValue
  };
}

export function convertAssessmentToDto(
  assessment: IAssessment
): IAssessmentDto {
  return {
    name: assessment.name,
    inputs: assessment.inputs,
    department: assessment.department,
    implemented: assessment.implemented
  };
}

export function isAssessmentFeasable(assessment: IAssessment): boolean {
  const outputs = assessment.outputs;
  if (!outputs) {
    return false;
  }
  return (
    outputs.effortToImplement != NoneFeasabilityResult.DIGITIZE_FIRST &&
    outputs.effortToImplement != NoneFeasabilityResult.LOW_FEASIBILITY &&
    outputs.effortToImplement != NoneFeasabilityResult.POSTPONE
  );
}
